import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useVendorAnalytics } from "@/hooks/useOrders"
import { useVendorProducts } from "@/hooks/useProducts"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts"
import { format } from "date-fns"
import { formatCurrency } from "@/lib/productDisplay"
import {
  DollarSign,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  Badge,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"

interface VendorPerformanceProps {}

const VendorPerformance: React.FC<VendorPerformanceProps> = () => {
  const { t } = useLanguage()
  const text = t.vendorPerformance
  const [days, setDays] = useState(30)
  const { data: analytics, isLoading } = useVendorAnalytics(days)
  const { data: products } = useVendorProducts()

  if (isLoading) return <div className="py-20 text-center">{text.loading}</div>

  const chartData =
    analytics?.salesTrend.map((d) => ({
      date: format(new Date(d.date), "dd/MM"),
      amount: d.amount,
    })) || []

  const barData =
    analytics?.topProducts.map((p) => ({
      name: p.productName,
      value: p.quantitySold,
      revenue: p.revenue,
    })) || []

  const COLORS = ["#10b981", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7"]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">{text.title}</h2>
        <Select value={days.toString()} onValueChange={(val) => setDays(parseInt(val))}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{text.days[7]}</SelectItem>
            <SelectItem value="30">{text.days[30]}</SelectItem>
            <SelectItem value="90">{text.days[90]}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.totalRevenue}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(analytics?.totalRevenue)}</div>
            <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-emerald-500" />
              <span>+12.5% vs prev</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.totalOrders}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalOrders}</div>
            <p className="mt-1 text-xs text-muted-foreground">{text.completedShipments}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.inventoryTurnover}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.inventoryTurnoverRatio.toFixed(2)}x
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{text.efficiencyRatio}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.inventoryToSales}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(analytics?.inventoryToSalesRatio! * 100).toFixed(1)}%
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{text.capitalTieUp}</p>
          </CardContent>
        </Card>
        <Card
          className={cn(
            analytics?.inventoryHealth.outOfStockCount! > 0
              ? "border-destructive/20 bg-destructive/5"
              : ""
          )}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.outOfStock}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.inventoryHealth.outOfStockCount}</div>
            {analytics?.inventoryHealth.outOfStockCount! > 0 && (
              <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3 w-3" /> {text.urgentRestock}
              </p>
            )}
          </CardContent>
        </Card>
        <Card
          className={cn(
            analytics?.inventoryHealth.lowStockCount! > 0 ? "border-amber-200 bg-amber-50" : ""
          )}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {text.lowStock}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.inventoryHealth.lowStockCount}</div>
            <p className="mt-1 text-xs text-amber-600">{text.belowThreshold}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {text.restockTitle}
            </CardTitle>
            <CardDescription>{text.restockDesc}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {products?.filter((p) => p.stockQuantity <= p.reorderPoint).length ? (
                products
                  ?.filter((p) => p.stockQuantity <= p.reorderPoint)
                  .map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between rounded-lg border bg-muted/30 p-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold">{p.name}</p>
                        <p className="text-xs uppercase text-muted-foreground">
                          Stock: {p.stockQuantity} / Trigger: {Math.ceil(p.reorderPoint)}
                        </p>
                      </div>
                      <Badge
                        variant={
                          p.stockQuantity <= p.safetyStockLevel ? "destructive" : "secondary"
                        }
                        className="text-xs"
                      >
                        {p.stockQuantity <= p.safetyStockLevel ? "CRITICAL" : "REORDER"}
                      </Badge>
                    </div>
                  ))
              ) : (
                <div className="py-6 text-center text-sm italic text-muted-foreground">
                  {text.healthyStock}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5 text-primary" />
              {text.revenueTrend}
            </CardTitle>
            <CardDescription>{text.days[days as keyof typeof text.days]}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => `$${val}`}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      formatter={(val: number) => [formatCurrency(val), text.revenue]}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="hsl(var(--primary))"
                      fillOpacity={1}
                      fill="url(#colorAmount)"
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center italic text-muted-foreground">
                  {text.empty}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-1">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5 text-primary" />
              {text.topProducts}
            </CardTitle>
            <CardDescription>{text.byQuantity}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              {barData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 40, right: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11 }}
                      width={100}
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
                      {barData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center italic text-muted-foreground">
                  {text.empty}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default VendorPerformance

